// Bilingual content for the Wolof language guide page (src/components/GuideWolof.tsx)
//
// Paragraphs that mix plain prose with inline <em>/<strong> emphasis (very common in this
// guide, since Wolof example words are set in italics/bold within French sentences) are
// modeled as an array of "parts" (WolofParagraph) so the component can still render real
// <em>/<strong> JSX elements while the text itself comes from this data file.
//
// IMPORTANT: Wolof-language example words, phrases and pronunciation guides (e.g. "jërejëf",
// "Nanga def ?", "Maa-ngi fi REKK, DJE-re-djef", digits like "Benn"/"Ñaar") are preserved
// verbatim in both the fr and en entries — only the surrounding French prose is translated.

export interface WolofTextPart {
  text: string;
  tag?: 'em' | 'strong';
}

export type WolofParagraph = WolofTextPart[];

export interface GuideWolofContent {
  pageTitle: string;
  breadcrumbHome: string;
  breadcrumbGuides: string;
  breadcrumbCurrent: string;
  h1: string;
  intro: string;

  whatIsTitle: string;
  whatIsP1: WolofParagraph;
  whatIsP2: WolofParagraph;
  whatIsP3: WolofParagraph;

  phonologyTitle: string;
  phonologyP1: WolofParagraph;
  phonologyP2: WolofParagraph;

  grammarTitle: string;
  grammarP1: WolofParagraph;
  grammarP2: WolofParagraph;

  greetingsTitle: string;
  greetingsP1: WolofParagraph;
  greetingsTableHeaders: [string, string, string];
  greetingsRows: [string, string, string][];

  familyTitle: string;
  familyTableHeaders: [string, string];
  familyRows: [string, string][];

  numbersTitle: string;
  numbersTableHeaders: [string, string, string, string];
  numbersRows: [string, string][];
  numbersP1: WolofParagraph;

  cultureTitle: string;
  cultureP1: WolofParagraph;
  cultureP2: WolofParagraph;
  cultureP3: string;

  practiceTitle: string;
  practiceP1: string;
  ctaStart: string;
  ctaBambara: string;
  ctaSwahili: string;

  footerHome: string;
  footerAbout: string;
  footerBambara: string;
  footerSwahili: string;
}

// Numbers are language-neutral (digit + Wolof numeral), so the rows are identical for fr/en.
const NUMBERS_ROWS: [string, string][] = [
  ['1 — Benn', '6 — Juróom benn'],
  ['2 — Ñaar', '7 — Juróom ñaar'],
  ['3 — Ñett', '8 — Juróom ñett'],
  ['4 — Ñent', '9 — Juróom ñent'],
  ['5 — Juróom', '10 — Fukk'],
];

export const GUIDE_WOLOF_CONTENT: Record<'fr' | 'en', GuideWolofContent> = {
  fr: {
    pageTitle: 'Apprendre le Wolof — Guide complet | JogoLinga',
    breadcrumbHome: 'JogoLinga',
    breadcrumbGuides: 'Guides des langues',
    breadcrumbCurrent: 'Wolof',
    h1: 'Apprendre le Wolof — Guide complet pour débutants',
    intro: "Le wolof est la langue du Sénégal, parlée par plus de 12 millions de personnes. Ce guide gratuit vous introduit à sa phonologie, sa grammaire et son vocabulaire essentiel.",

    whatIsTitle: "Qu'est-ce que le Wolof ?",
    whatIsP1: [
      { text: 'Le wolof (en wolof : ' },
      { text: 'Wolof', tag: 'em' },
      { text: ' ou ' },
      { text: 'Olof', tag: 'em' },
      { text: ") est la principale langue parlée au Sénégal. Bien qu'il ne soit pas la seule langue officielle du pays (le français occupe ce statut), le wolof est compris et parlé par environ " },
      { text: '90 % de la population sénégalaise', tag: 'strong' },
      { text: ", soit quelque 16 millions de locuteurs si l'on inclut les locuteurs secondaires. Il est également présent en Gambie (environ 20 % de la population), en Mauritanie et dans les diasporas africaines de France, d'Italie, d'Espagne et des États-Unis." },
    ],
    whatIsP2: [
      { text: 'Linguistiquement, le wolof appartient à la famille des ' },
      { text: 'langues atlantiques', tag: 'strong' },
      { text: ", sous-groupe de la grande famille Niger-Congo. Il est apparenté au sérère, au pulaar et au diola, mais présente des caractéristiques suffisamment originales pour constituer un groupe à part dans cette famille. Le wolof s'écrit avec l'alphabet latin depuis la codification officielle de 1971 ; certains locuteurs utilisent aussi l'" },
      { text: 'ajami', tag: 'em' },
      { text: ', une transcription en caractères arabes utilisée historiquement par les confréries soufies (Mourides, Tidianes).' },
    ],
    whatIsP3: [
      { text: 'La langue est indissociable de la culture sénégalaise. La notion de ' },
      { text: 'téranga', tag: 'strong' },
      { text: " (hospitalité) est au cœur des valeurs wolof, et les rituels de salutation en témoignent : une salutation complète peut durer plusieurs minutes, avec des questions sur la santé, la famille et les activités de chacun. Maîtriser ces formules, c'est accéder immédiatement au respect et à la confiance de vos interlocuteurs." },
    ],

    phonologyTitle: 'Phonologie — Les sons du Wolof',
    phonologyP1: [
      { text: 'La phonologie du wolof est accessible aux francophones, mais elle comporte quelques défis spécifiques. Le wolof distingue les ' },
      { text: 'consonnes simples et géminées', tag: 'strong' },
      { text: ' (doublées) qui changent le sens des mots : ' },
      { text: 'jëf', tag: 'em' },
      { text: ' signifie "acte" ou "action", tandis que ' },
      { text: 'jëëf', tag: 'em' },
      { text: ' signifie "cadeau". De même, les ' },
      { text: 'voyelles longues', tag: 'strong' },
      { text: ' sont distinctives : ' },
      { text: 'bët', tag: 'em' },
      { text: ' (œil) vs ' },
      { text: 'bëët', tag: 'em' },
      { text: ' (insecte type criquet).' },
    ],
    phonologyP2: [
      { text: 'Le son ' },
      { text: 'ë', tag: 'strong' },
      { text: ' (e ouvert, comme dans "le" en français mais plus central) est omniprésent en wolof. Le ' },
      { text: 'x', tag: 'strong' },
      { text: ' note une fricative uvulaire proche du "ch" allemand dans "Bach" ou du "j" espagnol dans "jota" — un son qui n\'existe pas en français standard. Le ' },
      { text: 'ñ', tag: 'strong' },
      { text: ' correspond au "gn" français, et le ' },
      { text: 'ng', tag: 'strong' },
      { text: ' en début de mot (comme dans ' },
      { text: 'ngor', tag: 'em' },
      { text: ', "dignité") est une nasale vélaire que les anglophones reconnaîtront dans le "ng" de "singing".' },
    ],

    grammarTitle: 'Grammaire — Les grandes structures du Wolof',
    grammarP1: [
      { text: 'La grammaire wolof diffère profondément du français sur plusieurs points fondamentaux. Contrairement aux langues indo-européennes, le wolof est une langue à ' },
      { text: 'classes nominales', tag: 'strong' },
      { text: ' : chaque nom appartient à une classe définie par un suffixe postposé (le déterminant vient après le nom). Il existe une dizaine de classes, chacune identifiée par une lettre : ' },
      { text: 'bi', tag: 'em' },
      { text: ' (xarit ' },
      { text: 'bi', tag: 'strong' },
      { text: " = l'ami), " },
      { text: 'gi', tag: 'em' },
      { text: ' (ker ' },
      { text: 'gi', tag: 'strong' },
      { text: ' = la maison), ' },
      { text: 'wi', tag: 'em' },
      { text: ' (góor ' },
      { text: 'wi', tag: 'strong' },
      { text: " = l'homme)." },
    ],
    grammarP2: [
      { text: 'Le système verbal wolof est ' },
      { text: 'aspectuel', tag: 'strong' },
      { text: " plutôt que temporel : le locuteur exprime d'abord si une action est accomplie (parfait) ou en cours / non accomplie (inaccompli), plus que le moment précis où elle a eu lieu. Le temps (passé, présent, futur) est souvent implicite ou précisé par des adverbes. La négation se forme avec le verbe " },
      { text: 'du', tag: 'em' },
      { text: ' et la marque aspectuelle correspondante.' },
    ],

    greetingsTitle: 'Salutations et expressions essentielles',
    greetingsP1: [
      { text: "Les salutations en wolof reflètent l'influence de la culture musulmane au Sénégal (environ 95 % de la population est musulmane). La salutation standard est empruntée à l'arabe : " },
      { text: 'Salamalekum', tag: 'em' },
      { text: ' (paix sur vous), à laquelle on répond ' },
      { text: 'Malekum salam', tag: 'em' },
      { text: '.' },
    ],
    greetingsTableHeaders: ['Français', 'Wolof', 'Prononciation'],
    greetingsRows: [
      ['Bonjour / Paix sur vous', 'Salamalekum', 'Sa-la-ma-LE-kum'],
      ['Réponse à la salutation', 'Malekum salam', 'Ma-LE-kum sa-LAM'],
      ['Comment vas-tu ?', 'Nanga def ?', 'Na-nga DEF ?'],
      ['Je vais bien, merci', 'Maa ngi fi rekk, jërejëf', 'Maa-ngi fi REKK, DJE-re-djef'],
      ["Comment tu t'appelles ?", 'Noo tudd ?', 'NO toud ?'],
      ['Je m\'appelle…', 'Maa ngi tudd…', 'Maa-ngi TOUD…'],
      ['Merci beaucoup', 'Jërejëf', 'DJE-re-DJEF'],
      ["S'il vous plaît", 'Bu la neex', 'Bu la NEKH'],
      ['Excuse-moi', 'Baal ma', 'BAAL ma'],
      ['Je ne comprends pas', 'Duma xam', 'DU-ma KHAM'],
      ['Au revoir', 'Ba beneen yoon', 'Ba be-NEEN YOON'],
      ['Bonne nuit', 'Fan bu bëgg', 'Fan bu BEGG'],
    ],

    familyTitle: 'Vocabulaire Wolof — La famille et les relations',
    familyTableHeaders: ['Français', 'Wolof'],
    familyRows: [
      ['Père', 'Baay bi'],
      ['Mère', 'Yaay bi'],
      ['Frère / Sœur', 'Mag bi (aîné) / Rak bi (cadet)'],
      ['Enfant', 'Doom bi'],
      ['Grand-père', 'Maam bu góor'],
      ['Grand-mère', 'Maam bu jigéen'],
      ['Mari', 'Jabar gi (épouse) / Jëkër gi (mari)'],
      ['Ami / Amie', 'Xarit bi'],
      ['Famille', 'Mbokk yi'],
      ['Maison', 'Ker gi'],
    ],

    numbersTitle: 'Les chiffres en Wolof',
    numbersTableHeaders: ['Chiffre', 'Wolof', 'Chiffre', 'Wolof'],
    numbersRows: NUMBERS_ROWS,
    numbersP1: [
      { text: "Le système numérique wolof est quinaire (base 5) : les nombres de 6 à 9 sont formés en ajoutant l'unité à " },
      { text: 'juróom', tag: 'em' },
      { text: ' (5). Vingt se dit ' },
      { text: 'ñaar fukk', tag: 'em' },
      { text: ' (deux fois dix), cent est ' },
      { text: 'téeméer', tag: 'em' },
      { text: ' et mille est ' },
      { text: 'junni', tag: 'em' },
      { text: '.' },
    ],

    cultureTitle: 'Culture Wolof — Griots, Téranga et Mourides',
    cultureP1: [
      { text: "Apprendre le wolof, c'est aussi plonger dans une culture orale d'une richesse exceptionnelle. Les " },
      { text: 'griots', tag: 'strong' },
      { text: ' (' },
      { text: 'géwël', tag: 'em' },
      { text: ' en wolof) sont les gardiens de la mémoire collective, maîtres de la parole, généalogistes, musiciens et poètes. Ils tiennent un rôle central dans les cérémonies (baptêmes, mariages, funérailles) et sont les dépositaires de l\'histoire des familles et des royaumes.' },
    ],
    cultureP2: [
      { text: 'La confrérie ' },
      { text: 'Mouride', tag: 'strong' },
      { text: ', fondée par Cheikh Ahmadou Bamba à la fin du XIXe siècle, est indissociable de la culture sénégalaise moderne. Sa capitale spirituelle, ' },
      { text: 'Touba', tag: 'strong' },
      { text: ', est la deuxième ville du Sénégal et accueille chaque année des millions de pèlerins lors du ' },
      { text: 'Magal', tag: 'em' },
      { text: '. La devise mouride — ' },
      { text: 'Ligeey', tag: 'em' },
      { text: " (travail) — est au cœur de l'éthique wolof." },
    ],
    cultureP3: "La musique sénégalaise, portée par des artistes comme Youssou N'Dour, Baaba Maal et Ismaël Lô, a fait connaître le wolof dans le monde entier. Les paroles de ces artistes constituent une ressource pédagogique précieuse : elles mêlent wolof moderne, références culturelles et poésie soufie dans une langue vivante et évolutive.",

    practiceTitle: 'Pratiquer le Wolof avec JogoLinga',
    practiceP1: "JogoLinga propose un parcours complet d'apprentissage du wolof : plus de 500 mots en vocabulaire thématique, des leçons de grammaire sur les classes nominales et le système aspectuel, des dialogues du quotidien (au marché, chez le médecin, en famille) et un système de révision par répétition espacée.",
    ctaStart: 'Commencer à apprendre le Wolof →',
    ctaBambara: 'Guide Bambara',
    ctaSwahili: 'Guide Swahili',

    footerHome: 'Accueil',
    footerAbout: 'À propos',
    footerBambara: 'Guide Bambara',
    footerSwahili: 'Guide Swahili',
  },

  en: {
    pageTitle: 'Learn Wolof — Complete Guide | JogoLinga',
    breadcrumbHome: 'JogoLinga',
    breadcrumbGuides: 'Language Guides',
    breadcrumbCurrent: 'Wolof',
    h1: 'Learn Wolof — A Complete Guide for Beginners',
    intro: 'Wolof is the language of Senegal, spoken by more than 12 million people. This free guide introduces you to its phonology, grammar, and essential vocabulary.',

    whatIsTitle: 'What Is Wolof?',
    whatIsP1: [
      { text: 'Wolof (in Wolof: ' },
      { text: 'Wolof', tag: 'em' },
      { text: ' or ' },
      { text: 'Olof', tag: 'em' },
      { text: ") is the main language spoken in Senegal. Although it is not the country's only official language (French holds that status), Wolof is understood and spoken by roughly " },
      { text: '90% of the Senegalese population', tag: 'strong' },
      { text: ', or some 16 million speakers when secondary speakers are included. It is also present in the Gambia (about 20% of the population), in Mauritania, and in African diaspora communities in France, Italy, Spain, and the United States.' },
    ],
    whatIsP2: [
      { text: 'Linguistically, Wolof belongs to the ' },
      { text: 'Atlantic languages', tag: 'strong' },
      { text: ', a subgroup of the larger Niger-Congo family. It is related to Serer, Pulaar, and Diola, but has distinctive enough features to form its own group within that family. Wolof has been written in the Latin alphabet since its official codification in 1971; some speakers also use ' },
      { text: 'ajami', tag: 'em' },
      { text: ', a transcription in Arabic script historically used by the Sufi brotherhoods (Mourides, Tijanis).' },
    ],
    whatIsP3: [
      { text: 'Language and Senegalese culture are inseparable. The concept of ' },
      { text: 'téranga', tag: 'strong' },
      { text: " (hospitality) lies at the heart of Wolof values, and greeting rituals reflect this: a full greeting can last several minutes, with questions about health, family, and everyone's activities. Mastering these formulas earns you immediate respect and trust from the people you speak with." },
    ],

    phonologyTitle: 'Phonology — The Sounds of Wolof',
    phonologyP1: [
      { text: 'Wolof phonology is accessible to French speakers, but it does have a few specific challenges. Wolof distinguishes between ' },
      { text: 'single and geminate consonants', tag: 'strong' },
      { text: ' (doubled), which change the meaning of words: ' },
      { text: 'jëf', tag: 'em' },
      { text: ' means "act" or "action", while ' },
      { text: 'jëëf', tag: 'em' },
      { text: ' means "gift". Likewise, ' },
      { text: 'long vowels', tag: 'strong' },
      { text: ' are distinctive: ' },
      { text: 'bët', tag: 'em' },
      { text: ' (eye) vs ' },
      { text: 'bëët', tag: 'em' },
      { text: ' (a locust-type insect).' },
    ],
    phonologyP2: [
      { text: 'The sound ' },
      { text: 'ë', tag: 'strong' },
      { text: ' (an open e, as in French "le" but more central) is everywhere in Wolof. The ' },
      { text: 'x', tag: 'strong' },
      { text: ' represents a uvular fricative close to the German "ch" in "Bach" or the Spanish "j" in "jota" — a sound that does not exist in standard French. The ' },
      { text: 'ñ', tag: 'strong' },
      { text: ' corresponds to the French "gn", and word-initial ' },
      { text: 'ng', tag: 'strong' },
      { text: ' (as in ' },
      { text: 'ngor', tag: 'em' },
      { text: ', "dignity") is a velar nasal that English speakers will recognize from the "ng" in "singing".' },
    ],

    grammarTitle: 'Grammar — The Main Structures of Wolof',
    grammarP1: [
      { text: 'Wolof grammar differs profoundly from French in several fundamental ways. Unlike Indo-European languages, Wolof is a ' },
      { text: 'noun-class language', tag: 'strong' },
      { text: ': every noun belongs to a class marked by a postposed suffix (the determiner comes after the noun). There are around a dozen classes, each identified by a letter: ' },
      { text: 'bi', tag: 'em' },
      { text: ' (xarit ' },
      { text: 'bi', tag: 'strong' },
      { text: ' = the friend), ' },
      { text: 'gi', tag: 'em' },
      { text: ' (ker ' },
      { text: 'gi', tag: 'strong' },
      { text: ' = the house), ' },
      { text: 'wi', tag: 'em' },
      { text: ' (góor ' },
      { text: 'wi', tag: 'strong' },
      { text: ' = the man).' },
    ],
    grammarP2: [
      { text: 'The Wolof verb system is ' },
      { text: 'aspectual', tag: 'strong' },
      { text: ' rather than tense-based: the speaker first expresses whether an action is completed (perfective) or ongoing / not completed (imperfective), rather than the precise moment it took place. Time (past, present, future) is often implicit or specified with adverbs. Negation is formed with the verb ' },
      { text: 'du', tag: 'em' },
      { text: ' plus the corresponding aspect marker.' },
    ],

    greetingsTitle: 'Greetings and Essential Expressions',
    greetingsP1: [
      { text: 'Wolof greetings reflect the influence of Muslim culture in Senegal (about 95% of the population is Muslim). The standard greeting is borrowed from Arabic: ' },
      { text: 'Salamalekum', tag: 'em' },
      { text: ' (peace be upon you), to which one replies ' },
      { text: 'Malekum salam', tag: 'em' },
      { text: '.' },
    ],
    greetingsTableHeaders: ['English', 'Wolof', 'Pronunciation'],
    greetingsRows: [
      ['Hello / Peace be upon you', 'Salamalekum', 'Sa-la-ma-LE-kum'],
      ['Reply to the greeting', 'Malekum salam', 'Ma-LE-kum sa-LAM'],
      ['How are you?', 'Nanga def ?', 'Na-nga DEF ?'],
      ["I'm fine, thank you", 'Maa ngi fi rekk, jërejëf', 'Maa-ngi fi REKK, DJE-re-djef'],
      ['What is your name?', 'Noo tudd ?', 'NO toud ?'],
      ['My name is…', 'Maa ngi tudd…', 'Maa-ngi TOUD…'],
      ['Thank you very much', 'Jërejëf', 'DJE-re-DJEF'],
      ['Please', 'Bu la neex', 'Bu la NEKH'],
      ['Excuse me', 'Baal ma', 'BAAL ma'],
      ["I don't understand", 'Duma xam', 'DU-ma KHAM'],
      ['Goodbye', 'Ba beneen yoon', 'Ba be-NEEN YOON'],
      ['Good night', 'Fan bu bëgg', 'Fan bu BEGG'],
    ],

    familyTitle: 'Wolof Vocabulary — Family and Relationships',
    familyTableHeaders: ['English', 'Wolof'],
    familyRows: [
      ['Father', 'Baay bi'],
      ['Mother', 'Yaay bi'],
      ['Brother / Sister', 'Mag bi (elder) / Rak bi (younger)'],
      ['Child', 'Doom bi'],
      ['Grandfather', 'Maam bu góor'],
      ['Grandmother', 'Maam bu jigéen'],
      ['Husband', 'Jabar gi (wife) / Jëkër gi (husband)'],
      ['Friend', 'Xarit bi'],
      ['Family', 'Mbokk yi'],
      ['House', 'Ker gi'],
    ],

    numbersTitle: 'Numbers in Wolof',
    numbersTableHeaders: ['Number', 'Wolof', 'Number', 'Wolof'],
    numbersRows: NUMBERS_ROWS,
    numbersP1: [
      { text: 'The Wolof number system is quinary (base 5): the numbers from 6 to 9 are formed by adding the unit to ' },
      { text: 'juróom', tag: 'em' },
      { text: ' (5). Twenty is ' },
      { text: 'ñaar fukk', tag: 'em' },
      { text: ' (twice ten), one hundred is ' },
      { text: 'téeméer', tag: 'em' },
      { text: ', and one thousand is ' },
      { text: 'junni', tag: 'em' },
      { text: '.' },
    ],

    cultureTitle: 'Wolof Culture — Griots, Téranga, and Mourides',
    cultureP1: [
      { text: 'Learning Wolof also means diving into an oral culture of exceptional richness. ' },
      { text: 'Griots', tag: 'strong' },
      { text: ' (' },
      { text: 'géwël', tag: 'em' },
      { text: ' in Wolof) are the keepers of collective memory — masters of the spoken word, genealogists, musicians, and poets. They play a central role in ceremonies (baptisms, weddings, funerals) and are the custodians of family and royal history.' },
    ],
    cultureP2: [
      { text: 'The ' },
      { text: 'Mouride', tag: 'strong' },
      { text: ' brotherhood, founded by Cheikh Ahmadou Bamba in the late 19th century, is inseparable from modern Senegalese culture. Its spiritual capital, ' },
      { text: 'Touba', tag: 'strong' },
      { text: ", is Senegal's second-largest city and welcomes millions of pilgrims each year for the " },
      { text: 'Magal', tag: 'em' },
      { text: '. The Mouride motto — ' },
      { text: 'Ligeey', tag: 'em' },
      { text: ' (work) — lies at the heart of Wolof ethics.' },
    ],
    cultureP3: "Senegalese music, carried by artists such as Youssou N'Dour, Baaba Maal, and Ismaël Lô, has brought Wolof to audiences worldwide. These artists' lyrics are a valuable learning resource: they blend modern Wolof, cultural references, and Sufi poetry in a living, evolving language.",

    practiceTitle: 'Practice Wolof with JogoLinga',
    practiceP1: "JogoLinga offers a complete Wolof learning path: over 500 words of thematic vocabulary, grammar lessons on noun classes and the aspect system, everyday dialogues (at the market, at the doctor's, with family) and a spaced repetition review system.",
    ctaStart: 'Start Learning Wolof →',
    ctaBambara: 'Bambara Guide',
    ctaSwahili: 'Swahili Guide',

    footerHome: 'Home',
    footerAbout: 'About',
    footerBambara: 'Bambara Guide',
    footerSwahili: 'Swahili Guide',
  },
};
